import axios from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';
import { buildExportParams } from './xbitRelayExport.helpers';

const base = 'xbit-relay';

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };
}

export type HotWalletBalance = {
  tokenAddress: string;
  symbol: string;
  decimals: number;
  balance: string;
  isNative: boolean;
  depositEnabled: boolean;
  withdrawalEnabled: boolean;
  error: string;
  alertConfigured: boolean;
  alertEnabled: boolean;
  /** Decimal string — balance level that raises a low-balance alert. */
  alertThreshold: string;
  /** Decimal string — balance level that triggers a rebalance. */
  rebalanceCeiling: string;
  /** Decimal string — balance to top up to. */
  rebalanceTarget: string;
  rebalanceEnabled: boolean;
};

export type HotWalletItem = {
  address: string;
  chainId: number;
  chainName: string;
  chainType: string;
  provider: string;
  balances: HotWalletBalance[];
};

export type ProviderItem = {
  provider: string;
  isEngineCredit: boolean;
  supportsWithdrawal: boolean;
};

export async function listHotWallets(provider?: string) {
  const headers = await authHeaders();
  const params: Record<string, string> = {};
  if (provider) params.provider = provider;
  return axios.get<{ wallets: HotWalletItem[] }>(`${baseUrl}/${base}/hot-wallets`, {
    headers,
    params,
  });
}

export async function listProviders() {
  const headers = await authHeaders();
  return axios.get<{ providers: ProviderItem[] }>(`${baseUrl}/${base}/providers`, { headers });
}

export type DepositStatus = 'PENDING' | 'PROCESSING' | 'CONFIRMING' | 'SUCCESS' | 'FAILED';

export type DepositItem = {
  id: string;
  createdAtUnixMs: string;
  updatedAtUnixMs: string;
  txHash: string;
  blockNumber: string;
  blockHash: string;
  logIndex: number;
  maker: string;
  chainId: number;
  timestamp: string;
  crawlTime: string;
  fee: string;
  fromAddress: string;
  toAddress: string;
  amount: string;
  token: string;
  status: DepositStatus | string;
  submittedTxHash: string;
  submittedTxIsOnchain?: boolean;
  errorMsg: string;
  rawAmount: string;
  transferAmount: string;
  nonce: string;
  targetAddress: string;
  fundWallet: string;
  retryTimes: string;
  userId: string;
  provider: string;
  tokenSymbol: string;
  tokenDecimals: number;
};

export type ListDepositsResponse = {
  items: DepositItem[];
  total: string;
  page: number;
  pageSize: number;
};

export type DepositFilters = {
  page?: number;
  pageSize?: number;
  status?: string;
  toAddress?: string;
  chainId?: number;
  token?: string;
  userId?: string;
  userAddress?: string;
  txHash?: string;
  timeFrom?: number;
  timeTo?: number;
  provider?: string;
};

export async function listDeposits(filters: DepositFilters = {}) {
  const headers = await authHeaders();
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return axios.get<ListDepositsResponse>(`${baseUrl}/${base}/deposits`, {
    headers,
    params,
  });
}

export type RetryDepositBody = {
  admin_id: string;
  signature: string;
  timestamp: number;
};

export type RetryDepositResponse = {
  status: boolean;
  message: string;
};

export async function retryDeposit(id: string, body: RetryDepositBody) {
  const headers = await authHeaders();
  return axios.post<RetryDepositResponse>(
    `${baseUrl}/${base}/deposits/${id}/retry`,
    body,
    { headers },
  );
}

export type DepositWalletItem = {
  id: string;
  userId: string;
  address: string;
  targetAddress: string;
  chain: string;
  provider: string;
  index: number;
  hdPath: string;
};

export type ListDepositWalletsResponse = {
  items: DepositWalletItem[];
  total: string;
  page: number;
  pageSize: number;
};

export type DepositWalletFilters = {
  page?: number;
  pageSize?: number;
  provider?: string;
  userId?: string;
  address?: string;
};

export async function listDepositWallets(filters: DepositWalletFilters = {}) {
  const headers = await authHeaders();
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params[key] = String(value);
  });
  return axios.get<ListDepositWalletsResponse>(`${baseUrl}/${base}/deposit-wallets`, {
    headers,
    params,
  });
}

export type ExportDepositsFilter = {
  provider?: string;
  timeFrom?: number;
  timeTo?: number;
  status?: string;
  toAddress?: string;
  chainId?: number;
  token?: string;
  userId?: string;
  userAddress?: string;
  txHash?: string;
};

// Export honors all on-screen list filters EXCEPT page/pageSize.
// provider omitted => ALL providers (no INTERNAL_PERPETUAL default, unlike the list).
export async function exportDeposits(filter: ExportDepositsFilter = {}): Promise<Blob> {
  const headers = await authHeaders();
  const params = buildExportParams(filter);
  const res = await axios.get<Blob>(`${baseUrl}/${base}/deposits/export`, {
    headers,
    params,
    responseType: 'blob',
  });
  return res.data;
}

export type WithdrawalProviderConfig = {
  provider: string;
  autoApprovalMaxAmount: string;
  frequencyWindowMin: number;
  frequencyMaxRequests: number;
  updatedAtUnixMs: string;
};

export async function listWithdrawalProviderConfigs() {
  const headers = await authHeaders();
  return axios.get<{ items: WithdrawalProviderConfig[] }>(
    `${baseUrl}/${base}/withdrawal-provider-configs`,
    { headers },
  );
}

export async function getWithdrawalProviderConfig(provider: string) {
  const headers = await authHeaders();
  return axios.get<{ item: WithdrawalProviderConfig }>(
    `${baseUrl}/${base}/withdrawal-provider-configs/${provider}`,
    { headers },
  );
}

export type ChainWithdrawalFeeBody = {
  chain_id: string;
  flat_fee: string;
};

export type UpdateWithdrawalProviderConfigBody = {
  auto_approval_max_amount: string;
  frequency_window_min: string;
  frequency_max_requests: string;
  chain_withdrawal_fees: ChainWithdrawalFeeBody[];
};

export async function updateWithdrawalProviderConfig(
  provider: string,
  body: UpdateWithdrawalProviderConfigBody,
) {
  const headers = await authHeaders();
  return axios.put(
    `${baseUrl}/${base}/withdrawal-provider-configs/${provider}`,
    body,
    { headers },
  );
}

/**
 * ⚠️ FULL REPLACE. wallet-service overwrites the whole config row on every call, so every field
 * is required and omitting `ceiling` / `target` / `rebalance_enabled` WIPES the stored rebalance
 * settings. Always build this from the current row — see `buildThresholdConfigBody` in the
 * hot-wallets module — never from the edited field alone.
 *
 * Decimal strings must match `^(?:0|[1-9]\d*)(?:\.\d+)?$`: no sign, no leading zeros, no
 * scientific notation, not empty. `'0'` and `'0.5'` are valid; `'-1'`, `'01'`, `'1e3'` give 400.
 */
export type UpdateThresholdBody = {
  chain_id: number;
  token_address: string;
  is_native: boolean;
  threshold: string;
  enabled: boolean;
  ceiling: string;
  target: string;
  rebalance_enabled: boolean;
};

export type UpdateThresholdConfigResponse = {
  success: boolean;
  config: {
    provider: string;
    chainId: number;
    tokenAddress: string;
    isNative: boolean;
    threshold: string;
    enabled: boolean;
    updatedAtUnixMs: string;
    ceiling: string;
    target: string;
    rebalanceEnabled: boolean;
  };
};

export async function updateHotWalletThreshold(provider: string, body: UpdateThresholdBody) {
  const headers = await authHeaders();
  return axios.put<UpdateThresholdConfigResponse>(
    `${baseUrl}/${base}/hot-wallets/${provider}/threshold-config`,
    body,
    { headers },
  );
}
